import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

export type PageIconTone =
  | 'violet'
  | 'indigo'
  | 'sky'
  | 'amber'
  | 'emerald'
  | 'rose'
  | 'purple'
  | 'teal'

export type PageIconSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE: Record<PageIconSize, { box: string; px: number; stroke: number }> = {
  sm: { box: 'h-8 w-8 rounded-lg', px: 15, stroke: 1.75 },
  md: { box: 'h-11 w-11 rounded-xl', px: 22, stroke: 1.5 },
  lg: { box: 'h-14 w-14 rounded-xl', px: 28, stroke: 1.35 },
  xl: { box: 'h-16 w-16 rounded-2xl', px: 32, stroke: 1.25 },
}

const TONE: Record<PageIconTone, { box: string; icon: string }> = {
  violet: {
    box: 'border-violet-500/40 bg-gradient-to-br from-violet-500/30 via-violet-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(139,92,246,0.55)]',
    icon: 'text-violet-100',
  },
  indigo: {
    box: 'border-indigo-500/40 bg-gradient-to-br from-indigo-500/30 via-indigo-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(99,102,241,0.55)]',
    icon: 'text-indigo-100',
  },
  sky: {
    box: 'border-sky-500/40 bg-gradient-to-br from-sky-500/30 via-sky-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(56,189,248,0.5)]',
    icon: 'text-sky-100',
  },
  amber: {
    box: 'border-amber-500/40 bg-gradient-to-br from-amber-500/30 via-amber-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(245,158,11,0.5)]',
    icon: 'text-amber-100',
  },
  emerald: {
    box: 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/30 via-emerald-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(52,211,153,0.5)]',
    icon: 'text-emerald-100',
  },
  rose: {
    box: 'border-rose-500/40 bg-gradient-to-br from-rose-500/30 via-rose-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(244,63,94,0.5)]',
    icon: 'text-rose-100',
  },
  purple: {
    box: 'border-purple-500/40 bg-gradient-to-br from-purple-500/30 via-purple-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(168,85,247,0.5)]',
    icon: 'text-purple-100',
  },
  teal: {
    box: 'border-teal-500/40 bg-gradient-to-br from-teal-500/30 via-teal-950/55 to-[#08080f] shadow-[0_0_32px_-8px_rgba(45,212,191,0.5)]',
    icon: 'text-teal-100',
  },
}

type Props = {
  icon: LucideIcon
  tone: PageIconTone
  size?: PageIconSize
  className?: string
  'aria-hidden'?: boolean
}

export function PageIconBadge({
  icon: Icon,
  tone,
  size = 'md',
  className,
  'aria-hidden': ariaHidden = true,
}: Props) {
  const s = SIZE[size] ?? SIZE.md
  const toneCls = TONE[tone] ?? TONE.indigo

  return (
    <span
      className={clsx(
        'relative inline-flex shrink-0 items-center justify-center border',
        'before:pointer-events-none before:absolute before:inset-px before:rounded-[inherit] before:bg-gradient-to-br before:from-white/[0.12] before:to-transparent',
        s.box,
        toneCls.box,
        className,
      )}
      aria-hidden={ariaHidden}
    >
      <Icon size={s.px} strokeWidth={s.stroke} className={clsx('relative z-[1]', toneCls.icon)} />
    </span>
  )
}
